import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';

export default function LandlordSidebar() {
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get('/landlord/dashboard')
      .then(res => setProperties(res.data.properties || []))
      .catch(() => {});
  }, []);

  const filtered = properties.filter(p =>
    search === '' ||
    `${p.name} ${p.city} ${p.state}`.toLowerCase().includes(search.toLowerCase())
  );

  const isActive = (path) => location.pathname === path;
  const isPropertyActive = (id) => location.pathname === `/landlord/properties/${id}`;

  const navItem = (to, icon, label) => (
    <a href={to} onClick={(e) => { e.preventDefault(); navigate(to); }}
      style={{
        padding: '10px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
        borderLeft: isActive(to) ? '3px solid var(--accent)' : '3px solid transparent',
        background: isActive(to) ? 'rgba(255,255,255,0.08)' : 'transparent',
        color: isActive(to) ? 'white' : 'rgba(255,255,255,0.65)', fontSize: 13,
        transition: 'all 0.1s', textDecoration: 'none',
      }}
      aria-current={isActive(to) ? 'page' : undefined}>
      <span aria-hidden="true">{icon}</span> {label}
    </a>
  );

  return (
    <nav className="sidebar" aria-label="Landlord navigation" style={{ width: 260, overflowY: 'auto', maxHeight: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column' }}>
      <div className="sidebar-section" aria-hidden="true">Portfolio</div>
      {navItem('/landlord/dashboard', '🏢', 'Portfolio Overview')}
      {navItem('/landlord/messages', '💬', 'Messages')}
      {navItem('/landlord/reports', '📊', 'Reports')}
      {navItem('/landlord/revenue', '💰', 'Revenue & Payouts')}

      <button type="button" aria-expanded={expanded} aria-controls="property-list"
        style={{ padding: '8px 16px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', width: '100%', background: 'none', border: 'none', fontFamily: 'inherit' }}
        onClick={() => setExpanded(!expanded)}>
        <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          My Properties ({properties.length})
        </span>
        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }} aria-hidden="true">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <>
          <div style={{ padding: '4px 12px 8px' }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              aria-label="Search properties"
              placeholder="Search properties..."
              style={{
                width: '100%', padding: '6px 10px', borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'rgba(255,255,255,0.08)', color: 'white',
                fontSize: 12, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
              }} />
          </div>

          {filtered.map(p => (
            <a key={p.id} href={`/landlord/properties/${p.id}`}
              onClick={(e) => { e.preventDefault(); navigate(`/landlord/properties/${p.id}`); }}
              aria-current={isPropertyActive(p.id) ? 'page' : undefined}
              style={{
                padding: '10px 20px', cursor: 'pointer', display: 'block', textDecoration: 'none',
                borderLeft: isPropertyActive(p.id) ? '3px solid var(--accent)' : '3px solid transparent',
                background: isPropertyActive(p.id) ? 'rgba(255,255,255,0.08)' : 'transparent',
                transition: 'all 0.1s',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: isPropertyActive(p.id) ? 'white' : 'rgba(255,255,255,0.75)' }}>
                  {p.name}
                </div>
                <div aria-label={`Status: ${p.status}`} style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginLeft: 6,
                  background: p.status === 'active' ? '#22c55e' : p.status === 'inactive' ? '#ef4444' : '#f59e0b'
                }} />
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 1 }}>{p.city}, {p.state}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 3 }}>
                <span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 600 }}>{p.active_units} active</span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }} aria-hidden="true">·</span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>{p.adoption_pct}% adoption</span>
              </div>
            </a>
          ))}

          {filtered.length === 0 && search && (
            <div style={{ padding: '8px 20px', fontSize: 12, color: 'rgba(255,255,255,0.6)' }} role="status">
              No properties match "{search}"
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: 'auto', padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
          Support
        </div>
        <a href="mailto:support@affordabletelecom.com" style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>support@affordabletelecom.com</a>
      </div>
    </nav>
  );
}
